/**
 * Context Learning Dashboard Component
 * =====================================
 * 
 * Shows how the system is "learning" from observed outcomes.
 * Displays confidence trends and accuracy metrics.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Brain, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BarChart3,
  Target,
  Lightbulb,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

interface EventConfidenceMetrics {
  event_id: string;
  event_name: string;
  event_type: string;
  current_confidence: number;
  outcome_count: number;
  matched_count: number;
  accuracy_rate: number | null;
  last_outcome_at: string | null;
  trending: 'improving' | 'declining' | 'stable' | 'insufficient_data';
}

interface OverallLearningMetrics {
  total_events: number;
  events_with_outcomes: number;
  total_outcomes: number;
  overall_accuracy: number | null;
  high_confidence_events: number;
  low_confidence_events: number;
  avg_confidence: number;
}

interface ConfidenceResponse {
  success: boolean;
  events: EventConfidenceMetrics[];
  overall: OverallLearningMetrics;
  insights: string[];
  learning_status: 'healthy' | 'needs_data' | 'needs_calibration';
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function fetchConfidenceMetrics(): Promise<ConfidenceResponse> {
  const res = await fetch('/api/admin/context-events/confidence');
  if (!res.ok) throw new Error('Failed to fetch confidence metrics');
  return res.json();
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = {
    healthy: { color: 'emerald', icon: <CheckCircle className="w-4 h-4" />, label: 'Healthy' },
    needs_data: { color: 'amber', icon: <AlertTriangle className="w-4 h-4" />, label: 'Needs More Data' },
    needs_calibration: { color: 'rose', icon: <XCircle className="w-4 h-4" />, label: 'Needs Calibration' },
  };
  
  const c = config[status as keyof typeof config] || config.needs_data;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-${c.color}-500/20 text-${c.color}-400 border border-${c.color}-500/30`}>
      {c.icon}
      {c.label}
    </span>
  );
};

const TrendingBadge: React.FC<{ trend: string }> = ({ trend }) => {
  const config = {
    improving: { color: 'emerald', icon: <TrendingUp className="w-3 h-3" />, label: 'Improving' },
    declining: { color: 'rose', icon: <TrendingDown className="w-3 h-3" />, label: 'Declining' },
    stable: { color: 'gray', icon: <Minus className="w-3 h-3" />, label: 'Stable' },
    insufficient_data: { color: 'gray', icon: <AlertTriangle className="w-3 h-3" />, label: 'Needs Data' },
  };
  
  const c = config[trend as keyof typeof config] || config.insufficient_data;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-${c.color}-500/20 text-${c.color}-400`}>
      {c.icon}
      {c.label}
    </span>
  );
};

const ConfidenceBar: React.FC<{ confidence: number }> = ({ confidence }) => {
  const pct = Math.round(confidence * 100);
  const color = confidence >= 0.7 ? 'bg-emerald-500' : confidence >= 0.4 ? 'bg-amber-500' : 'bg-rose-500';
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-medium text-white w-12 text-right">{pct}%</span>
    </div>
  );
};

const MetricCard: React.FC<{ 
  icon: React.ReactNode; 
  label: string; 
  value: string | number; 
  subtext?: string;
  color?: string;
}> = ({ icon, label, value, subtext, color = 'gray' }) => (
  <div className={`bg-${color}-500/10 border border-${color}-500/30 rounded-lg p-4`}>
    <div className={`flex items-center gap-2 text-${color}-400 mb-2`}>
      {icon}
      <span className="text-sm">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const ContextLearning: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['context-events', 'confidence'],
    queryFn: fetchConfidenceMetrics,
    refetchInterval: 60000,
  });
  
  if (isLoading) {
    return (
      <div className="text-center py-12 text-gray-400">
        Loading learning metrics...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12 text-rose-400">
        Failed to load learning metrics
      </div>
    );
  }
  
  if (!data) return null;
  
  const { events, overall, insights, learning_status } = data;
  
  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Brain className="w-6 h-6 text-amber-400" />
            System Learning Status
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            How well PIQ's context predictions match reality
          </p>
        </div>
        <StatusBadge status={learning_status} />
      </div>
      
      {/* Overall Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          icon={<BarChart3 className="w-4 h-4" />}
          label="Total Events"
          value={overall.total_events}
          subtext={`${overall.events_with_outcomes} with outcomes`}
          color="gray"
        />
        <MetricCard
          icon={<Target className="w-4 h-4" />}
          label="Overall Accuracy"
          value={overall.overall_accuracy !== null ? `${Math.round(overall.overall_accuracy * 100)}%` : 'N/A'}
          subtext={`${overall.total_outcomes} outcomes recorded`}
          color={overall.overall_accuracy !== null && overall.overall_accuracy >= 0.7 ? 'emerald' : 'amber'}
        />
        <MetricCard
          icon={<CheckCircle className="w-4 h-4" />}
          label="High Confidence"
          value={overall.high_confidence_events}
          subtext="Events ≥70% confidence"
          color="emerald"
        />
        <MetricCard
          icon={<AlertTriangle className="w-4 h-4" />}
          label="Low Confidence"
          value={overall.low_confidence_events}
          subtext="Events ≤30% confidence"
          color="rose"
        />
      </div>
      
      {/* Insights */}
      {insights.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <Lightbulb className="w-4 h-4" />
            <span className="font-medium">Learning Insights</span>
          </div>
          <ul className="space-y-1">
            {insights.map((insight, i) => (
              <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                <span className="text-amber-500">•</span>
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Per-Event Confidence Table */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700/50">
          <h3 className="font-medium text-white">Event Confidence Breakdown</h3>
        </div>
        
        {events.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No events to display. Add events to start tracking confidence.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-700/50">
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Confidence</th>
                  <th className="px-4 py-3 font-medium">Outcomes</th>
                  <th className="px-4 py-3 font-medium">Accuracy</th>
                  <th className="px-4 py-3 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {events.map(event => (
                  <tr key={event.event_id} className="hover:bg-gray-700/20">
                    <td className="px-4 py-3">
                      <span className="font-medium text-white">{event.event_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400 capitalize">
                        {event.event_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 w-48">
                      <ConfidenceBar confidence={event.current_confidence} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-400">
                        {event.matched_count}/{event.outcome_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${
                        event.accuracy_rate === null ? 'text-gray-500' :
                        event.accuracy_rate >= 0.7 ? 'text-emerald-400' :
                        event.accuracy_rate >= 0.4 ? 'text-amber-400' : 'text-rose-400'
                      }`}>
                        {event.accuracy_rate !== null 
                          ? `${Math.round(event.accuracy_rate * 100)}%` 
                          : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <TrendingBadge trend={event.trending} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* How It Works */}
      <div className="bg-gray-800/30 border border-gray-700/30 rounded-lg p-4">
        <h3 className="font-medium text-white mb-2">How Learning Works</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <p>
            <strong className="text-gray-300">1. You add context events</strong> — Tell PIQ about promos, 
            seasonality, competitor activity, and other factors that affect your business.
          </p>
          <p>
            <strong className="text-gray-300">2. PIQ uses this context</strong> — When analyzing performance, 
            PIQ considers active events and adjusts severity accordingly.
          </p>
          <p>
            <strong className="text-gray-300">3. Outcomes are recorded</strong> — After events end, 
            PIQ compares predictions to actual results.
          </p>
          <p>
            <strong className="text-gray-300">4. Confidence updates</strong> — Events that consistently 
            match predictions gain confidence. Those that don't lose confidence.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContextLearning;
